import React, { useEffect, useState } from "react";
import PendingFollowupListing from "./PendingFollowupListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setTotalItems,
  setItems,
} from "src/redux/slices/PendingFollowupSlice";
import Tooltip from "@mui/material/Tooltip";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";
import { Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import { isCheckActionsPermission, isCheckColumnPermission } from "src/helpers/userAccessFun";
import { useGetAllPendingFollowUpQuery } from "src/services/FollowUpServices";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMCopyToClipboard from "src/components/UI/atoms/ATMCopyToClipboard/ATMCopyToClipboard";
import { IoCopyOutline } from "react-icons/io5";
import { showToast } from "src/utils/showToaster";

type Props = {};

const PendingFollowupListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isCopied, setIsCopied] = useState(false);
  const { userType } = useSelector((state: RootState) => state.auth);

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.pendingFollowup);

  const { data, isLoading, isFetching } = useGetAllPendingFollowUpQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: [
      "is_communicated",
      "communication_summary",
      "communication_method",
      "interest_level",
      "next_followUp_required",
      "contact_them",
      "next_followUp_date",
      "new_batch_start",
      "followup_batch_start_course",
      "next_followup_reason",
      "interested_course",
      "disposition_one",
      "disposition_two",
    ],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, data]);

  const columns: columnTypes[] = [
    {
      field: "createdAt",
      headerName: "Date - Time",
      extraClasses: "py-1",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatedDateTimeIntoIst(row.createdAt, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatedDateTimeIntoIst(row.createdAt, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "contact_them",
      extraClasses: "py-2 justify-center",
      headerName: `Contact On`,
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <span>
          {" "}
          {row?.contact_them === "ON_EVENT" ? (
            row?.followup_batch_start_course?.map((course: any) => {
              return (
                <Chip
                  key={course?._id}
                  label={course?.courseName}
                  className="me-2 my-1"
                />
              );
            })
          ) : (
            <div>
              <div className="text-slate-700 font-medium">
                {formatedDateTimeIntoIst(
                  row?.next_followUp_date,
                  "DD MMM yyyy"
                )}
              </div>
              <div className="text-[13px] font-medium text-slate-400">
                {row?.next_followup_time}
              </div>
            </div>
          )}{" "}
        </span>
      ),
    },
    {
      field: "name",
      extraClasses: "py-2 justify-center",
      headerName: "Name",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => (
        <span className="capitalize"> {row?.name} </span>
      ),
    },
    {
      field: "mobile",
      headerName: "Mobile Number",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses: "min-w-[150px] py-2",
      renderCell: (row: any) => (
        <div
          onClick={(e: any) => {
            e.stopPropagation();
          }}
          className="bg-stone-100 p-[6px] flex justify-between items-center rounded-xl  text-sm gap-4 cursor-default"
        >
          {row?.mobile}
          <ATMCopyToClipboard
            tooltipTitle={isCopied ? "Copied" : "Copy"}
            copyText={row?.mobile}
            onCopy={() => {
              setIsCopied(true);
              setTimeout(() => {
                setIsCopied(false);
              }, 1000);
            }}
          >
            <IoCopyOutline className="cursor-pointer" />
          </ATMCopyToClipboard>
        </div>
      ),
    },
    {
      field: "communication_summary",
      headerName: "Summary",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => {
        return (
          <Tooltip title={row?.communication_summary}>
            <span>{row?.communication_summary}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "next_followUp_required",
      extraClasses: "py-2 justify-center",
      headerName: "Next FollowUp Required",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => <span> {row?.next_followUp_required} </span>,
    },
    {
      field: "is_communicated",
      extraClasses: "py-2 justify-center",
      headerName: "Is Communicated",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => <span> {row?.is_communicated} </span>,
    },
    {
      field: "add",
      headerName: "Action",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses: "min-w-[150px] flex justify-center",
      renderCell: (row: any) => {
        return (
          <ATMLoadingButton
            className="w-16"
            onClick={(e: any) => {
              e.stopPropagation();
              if (userType === "ADMIN") {
                navigate(`/inquiries/edit/${row?.inquiry_id}/followup/add`);
              } else {
                const isExist = isCheckActionsPermission({ moduleName: UserModuleNameTypes.INQUIRIES, actionName: UserModuleActionTypes.INQUIRIES_EDIT_FOLLOWUP_ADD })
                if (isExist) {
                  navigate(`/inquiries/edit/${row?.inquiry_id}/followup/add`);
                } else {
                  showToast("error", "Access Denied")
                }
              }
            }}
          >
            Add
          </ATMLoadingButton>
        );
      },
    },
  ];

  return (
    <div>
      <PendingFollowupListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.PENDING_FOLLOWUP,
          actionName: UserModuleActionTypes.LIST,
          columns: columns,
       })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />
    </div>
  );
};

export default PendingFollowupListingWrapper;
